/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 * Licensed under the VIJAYA 1.0 License.
 *
 * WebhookEndpointsPage — Admin page for managing outbound webhook endpoints.
 *
 * Product design rationale:
 *   Webhooks let app admins connect Arcee record events to any external system —
 *   their own backend, Slack, CRMs, automation tools like n8n or Make.com.
 *   An endpoint is a saved URL record (name + URL + auth) that can be reused across
 *   many event triggers. Admins create them here once, then pick them by name in the
 *   Event Framework trigger form.
 *
 * Page sections:
 *   1. Endpoint list — all saved endpoints for this app
 *   2. Create / Edit modal — full form with dynamic auth config fields
 *   3. Test panel — fire a test request and see the live result inline
 *   4. Logs modal — paginated delivery history per endpoint
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import { useAlertDialog } from '../components/common/AlertDialogContext';
import Paginator from '../components/common/Paginator';

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});

// ─── Constants ──────────────────────────────────────────────────────────────

const HTTP_METHODS = ['POST', 'GET', 'PUT', 'PATCH', 'DELETE'];

const AUTH_TYPES = [
    { value: 'none', label: 'No Authentication', icon: 'bi-slash-circle' },
    { value: 'api_key', label: 'API Key', icon: 'bi-key' },
    { value: 'jwt', label: 'JWT (HS256)', icon: 'bi-shield-lock' },
    { value: 'oauth2', label: 'OAuth2 Client Credentials', icon: 'bi-lock' },
];

const METHOD_COLORS = {
    GET: { bg: '#dcfce7', color: '#166534' },
    POST: { bg: '#dbeafe', color: '#1d4ed8' },
    PUT: { bg: '#fef3c7', color: '#b45309' },
    PATCH: { bg: '#ede9fe', color: '#6d28d9' },
    DELETE: { bg: '#fee2e2', color: '#b91c1c' },
};

const STATUS_CONFIG = {
    success: { bg: '#dcfce7', color: '#166534', icon: 'bi-check-circle-fill', label: 'Success' },
    failed: { bg: '#fee2e2', color: '#b91c1c', icon: 'bi-x-circle-fill', label: 'Failed' },
    pending: { bg: '#fef3c7', color: '#b45309', icon: 'bi-clock-fill', label: 'Pending' },
    retrying: { bg: '#ede9fe', color: '#6d28d9', icon: 'bi-arrow-repeat', label: 'Retrying' },
};

const EMPTY_FORM = {
    name: '',
    url: '',
    http_method: 'POST',
    body_format: 'json',
    auth_type: 'none',
    auth_config: {},
    custom_headers: [],      // [{key:'', value:''}]
    timeout_seconds: 30,
    retry_count: 3,
    is_active: true,
    enable_signing: false,
    include_meta: false,
    jolt_enabled: false,
    jolt_spec: '',           // JSON string in the textarea
    jolt_on_failure: 'open',
};

const BODY_FORMATS = [
    { value: 'json',        label: 'JSON',         description: 'application/json' },
    { value: 'form_data',   label: 'Form Data',    description: 'application/x-www-form-urlencoded' },
    { value: 'query_params',label: 'Query Params', description: 'Sent as URL query string (GET only)' },
];

// All Arcee event types — used to populate the preview panel's event selector.
// Each entry: { value: '<event_key>', label: '<Display Label>', group: '<Group>' }
const EVENT_TYPES = [
    // Record events
    { value: 'record.created',  label: 'Record Created',       group: 'Record' },
    { value: 'record.updated',  label: 'Record Updated',       group: 'Record' },
    { value: 'record.deleted',  label: 'Record Deleted',       group: 'Record' },
    // Table events
    { value: 'table.created',   label: 'Table Created',        group: 'Table / Field' },
    { value: 'table.updated',   label: 'Table Updated',        group: 'Table / Field' },
    { value: 'table.deleted',   label: 'Table Deleted',        group: 'Table / Field' },
    { value: 'field.created',   label: 'Field Created',        group: 'Table / Field' },
    { value: 'field.updated',   label: 'Field Updated',        group: 'Table / Field' },
    { value: 'field.deleted',   label: 'Field Deleted',        group: 'Table / Field' },
    // Application events
    { value: 'application.deleted',   label: 'Application Deleted',   group: 'Application' },
    { value: 'application.published', label: 'Application Published', group: 'Application' },
    { value: 'application.updated',   label: 'Application Updated',   group: 'Application' },
    // Export events
    { value: 'export.requested', label: 'Export Requested', group: 'Export' },
    { value: 'export.completed', label: 'Export Completed', group: 'Export' },
    { value: 'export.failed',    label: 'Export Failed',    group: 'Export' },
    // User events
    { value: 'user.created',          label: 'User Created',          group: 'User' },
    { value: 'user.updated',          label: 'User Updated',          group: 'User' },
    { value: 'user.deleted',          label: 'User Deleted',          group: 'User' },
    { value: 'user.login_failed',     label: 'User Login Failed',     group: 'User' },
    { value: 'user.password_changed', label: 'Password Changed',      group: 'User' },
    { value: 'user.role_assigned',    label: 'Role Assigned',         group: 'User' },
    { value: 'user.role_revoked',     label: 'Role Revoked',          group: 'User' },
    { value: 'user.group_assigned',   label: 'Group Assigned',        group: 'User' },
    { value: 'user.group_removed',    label: 'Group Removed',         group: 'User' },
    // Test event
    { value: 'test.fired', label: 'Test Fired', group: 'Webhook' },
];

// Group EVENT_TYPES by their group key (used for <optgroup> rendering)
const EVENT_TYPE_GROUPS = EVENT_TYPES.reduce((acc, et) => {
    if (!acc[et.group]) acc[et.group] = [];
    acc[et.group].push(et);
    return acc;
}, {});

// ─── Auth config field definitions ──────────────────────────────────────────

function getAuthFields(authType) {
    switch (authType) {
        case 'api_key':
            return [
                { key: 'header', label: 'Header / Param Name', type: 'text', placeholder: 'X-API-Key', required: true, maxLength: 50 },
                { key: 'value', label: 'Key Value', type: 'password', placeholder: '••••••', required: true, maxLength: 128 },
                {
                    key: 'placement', label: 'Send In', type: 'select', options: [
                        { value: 'header', label: 'HTTP Header' },
                        { value: 'query', label: 'Query Parameter' },
                    ]
                },
            ];
        case 'jwt':
            return [
                { key: 'secret', label: 'Signing Secret', type: 'password', placeholder: 'your-secret-key', required: true, maxLength: 64 },
                { key: 'expiry_seconds', label: 'Token Expiry (sec)', type: 'text', placeholder: '3600 (default, max 86400)', required: false, maxLength: 5 },
            ];
        case 'oauth2':
            return [
                { key: 'token_url', label: 'Token URL', type: 'url', placeholder: 'https://auth.example.com/oauth/token', required: true, maxLength: 512 },
                { key: 'client_id', label: 'Client ID', type: 'text', placeholder: 'my-client-id', required: true, maxLength: 128 },
                { key: 'client_secret', label: 'Client Secret', type: 'password', placeholder: '••••••', required: true, maxLength: 256 },
                { key: 'scope', label: 'Scope', type: 'text', placeholder: 'read write (space-separated, optional)', required: false },
            ];
        default:
            return [];
    }
}

// Convert flat form state → auth_config object for API
function buildAuthConfig(authType, authValues) {
    if (authType === 'none') return {};
    const fields = getAuthFields(authType);
    const config = {};
    for (const f of fields) {
        if (authValues[f.key] !== undefined && authValues[f.key] !== '') {
            config[f.key] = f.type === 'number'
                ? parseInt(authValues[f.key], 10) || 0
                : authValues[f.key];
        }
    }
    return config;
}

// Convert headers array [{key,value}] → object
function headersArrayToObj(arr) {
    const obj = {};
    for (const row of arr) {
        if (row.key.trim()) obj[row.key.trim()] = row.value;
    }
    return obj;
}

// Convert headers object → array
function headersObjToArray(obj) {
    if (!obj || typeof obj !== 'object') return [];
    return Object.entries(obj).map(([key, value]) => ({ key, value }));
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function WebhookEndpointsPage() {
    const { theme } = useTheme();
    const { id: applicationId } = useParams();
    const navigate = useNavigate();
    const { showAlertDialog } = useAlertDialog();

    const [endpoints, setEndpoints] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal states
    const [showForm, setShowForm] = useState(false);
    const [editingEndpoint, setEditingEndpoint] = useState(null);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [authValues, setAuthValues] = useState({});
    const [formErrors, setFormErrors] = useState([]);
    const [saving, setSaving] = useState(false);

    // Inline placeholder warning confirmation
    const [confirmingToggleId, setConfirmingToggleId] = useState(null);

    // Test panel
    const [testingId, setTestingId] = useState(null);
    const [testResult, setTestResult] = useState(null);  // { endpointId, success, log }
    const [testLoading, setTestLoading] = useState(false);

    // Logs modal
    const [logsEndpoint, setLogsEndpoint] = useState(null);
    const [logs, setLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [logsPage, setLogsPage] = useState(1);
    const [logsTotalPages, setLogsTotalPages] = useState(1);
    const [logsTotal, setLogsTotal] = useState(0);
    const LOGS_PAGE_SIZE = 10;

    // ── Data loading ────────────────────────────────────────────────────────

    const fetchEndpoints = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const resp = await apiHelper.apiCall(
                `/arcee/webhooks/?application_id=${applicationId}`,
                { method: 'GET' }
            );
            const contentType = resp.headers.get('content-type') || '';
            if (!contentType.includes('application/json')) {
                throw new Error(`Server error (HTTP ${resp.status}). Please check that database migrations are applied and the server is running correctly.`);
            }
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.error || 'Failed to load webhook endpoints.');
            setEndpoints(data);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [applicationId]);

    useEffect(() => { fetchEndpoints(); }, [fetchEndpoints]);

    // ── Form helpers ─────────────────────────────────────────────────────────

    function openCreateForm() {
        setEditingEndpoint(null);
        setFormData(EMPTY_FORM);
        setAuthValues({});
        setFormErrors([]);
        setShowForm(true);
    }

    function openEditForm(ep) {
        setEditingEndpoint(ep);
        const headers = headersObjToArray(ep.custom_headers || {});
        setFormData({
            name: ep.name,
            url: ep.url,
            http_method: ep.http_method,
            body_format: ep.body_format || 'json',
            auth_type: ep.auth_type,
            auth_config: ep.auth_config || {},
            custom_headers: headers.length ? headers : [],
            timeout_seconds: ep.timeout_seconds,
            retry_count: ep.retry_count,
            is_active: ep.is_active,
            enable_signing: ep.signing_enabled || false,
            include_meta: ep.include_meta === true,
            jolt_enabled: ep.jolt_enabled || false,
            jolt_spec: ep.jolt_spec ? JSON.stringify(ep.jolt_spec, null, 2) : '',
            jolt_on_failure: ep.jolt_on_failure || 'open',
        });
        // Populate auth values (masked secrets show as empty — user must re-enter to change)
        const rawConfig = ep.auth_config || {};
        const initialAuth = {};
        for (const f of getAuthFields(ep.auth_type)) {
            initialAuth[f.key] = rawConfig[f.key] === '***' ? '' : (rawConfig[f.key] ?? '');
        }
        setAuthValues(initialAuth);
        setFormErrors([]);
        setShowForm(true);
    }

    function closeForm() {
        setShowForm(false);
        setEditingEndpoint(null);
    }

    function handleFieldChange(key, value) {
        setFormData(prev => ({ ...prev, [key]: value }));
        if (key === 'auth_type') {
            setAuthValues({});
        }
    }

    function handleAuthChange(key, value) {
        setAuthValues(prev => ({ ...prev, [key]: value }));
    }

    // Custom headers editor
    function addHeader() {
        setFormData(prev => ({ ...prev, custom_headers: [...prev.custom_headers, { key: '', value: '' }] }));
    }
    function removeHeader(i) {
        setFormData(prev => ({ ...prev, custom_headers: prev.custom_headers.filter((_, idx) => idx !== i) }));
    }
    function updateHeader(i, field, val) {
        setFormData(prev => {
            const headers = [...prev.custom_headers];
            headers[i] = { ...headers[i], [field]: val };
            return { ...prev, custom_headers: headers };
        });
    }

    // ── Save (create / update) ───────────────────────────────────────────────

    function handleSave() {
        _doSave();
    }

    async function _doSave() {
        const errors = [];
        const name = formData.name.trim();
        const url  = formData.url.trim();
        if (!name)                                          errors.push("Endpoint Name is required.");
        else if (name.length < 5)                          errors.push("Endpoint Name must be at least 5 characters.");
        else if (name.length > 60)                         errors.push("Endpoint Name must be 60 characters or fewer.");
        else if (!/^[A-Za-z0-9 _.()-]+$/.test(name))      errors.push("Endpoint Name may only contain letters, digits, spaces, hyphens, underscores, dots, and parentheses.");
        if (!url)                                          errors.push("Target URL is required.");
        else if (!/^https?:\/\//i.test(url))               errors.push("Target URL must start with http:// or https://");
        else if (/\s/.test(url))                           errors.push("Target URL must not contain spaces.");

        const authFields = getAuthFields(formData.auth_type);
        for (const f of authFields) {
            const val = authValues[f.key] || '';
            const alreadySavedOnServer = editingEndpoint && f.type === 'password' && editingEndpoint.auth_config?.[f.key] === '***';

            // Required check
            if (f.required && !val && !alreadySavedOnServer) {
                errors.push(`${f.label} is required for ${formData.auth_type} auth.`);
                continue;
            }

            if (!val || alreadySavedOnServer) continue;  // nothing more to validate

            // Header name: only letters and hyphens
            if (f.key === 'header' && !/^[A-Za-z0-9-]+$/.test(val)) {
                errors.push('Header / Param Name may only contain letters, digits, and hyphens.');
            }
            // API key value: no spaces
            if (f.key === 'value' && /\s/.test(val)) {
                errors.push('Key Value must not contain spaces.');
            }
            // JWT token expiry range
            if (f.key === 'expiry_seconds') {
                const secs = parseInt(val, 10);
                if (isNaN(secs) || secs < 30) errors.push('Token Expiry must be at least 30 seconds.');
                else if (secs > 86400)         errors.push('Token Expiry must be 86400 seconds (24 hours) or less.');
            }
            // Scope: letters and spaces only
            if (f.key === 'scope' && !/^[A-Za-z ]*$/.test(val)) {
                errors.push('Scope may only contain letters and spaces.');
            }
            // Max length
            if (f.maxLength && val.length > f.maxLength) {
                errors.push(`${f.label} must be ${f.maxLength} characters or fewer.`);
            }
        }

        // Jolt spec validation
        let parsedJoltSpec = null;
        if (formData.jolt_enabled) {
            const specStr = formData.jolt_spec.trim();
            if (!specStr) {
                errors.push('Jolt Spec is required when Payload Transform is enabled.');
            } else {
                try {
                    parsedJoltSpec = JSON.parse(specStr);
                    if (!Array.isArray(parsedJoltSpec)) {
                        errors.push('Jolt Spec must be a JSON array of operation objects.');
                    }
                } catch (e) {
                    errors.push('Jolt Spec is not valid JSON: ' + e.message);
                }
            }
        }

        if (errors.length) { setFormErrors(errors); return; }
        setFormErrors([]);
        setSaving(true);

        const rawTimeout = parseInt(formData.timeout_seconds, 10);
        const rawRetry = parseInt(formData.retry_count, 10);
        const clampedTimeout = isNaN(rawTimeout) ? 30 : Math.min(120, Math.max(5, rawTimeout));
        const clampedRetry = isNaN(rawRetry) ? 0 : Math.min(10, Math.max(0, rawRetry));

        const payload = {
            application_id: parseInt(applicationId, 10),
            name: formData.name.trim(),
            url: formData.url.trim(),
            http_method: formData.http_method,
            body_format: formData.body_format,
            auth_type: formData.auth_type,
            auth_config: buildAuthConfig(formData.auth_type, authValues),
            custom_headers: headersArrayToObj(formData.custom_headers),
            timeout_seconds: clampedTimeout,
            retry_count: clampedRetry,
            is_active: formData.is_active,
            enable_signing: formData.enable_signing,
            include_meta: formData.include_meta,
            jolt_enabled: formData.jolt_enabled,
            jolt_spec: parsedJoltSpec,
            jolt_on_failure: formData.jolt_on_failure,
        };

        try {
            let resp;
            if (editingEndpoint) {
                resp = await apiHelper.apiCall(
                    `/arcee/webhooks/${editingEndpoint.id}/`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload),
                    }
                );
            } else {
                resp = await apiHelper.apiCall(
                    '/arcee/webhooks/create/',
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload),
                    }
                );
            }
            let data;
            const contentType = resp.headers.get('content-type') || '';
            if (contentType.includes('application/json')) {
                data = await resp.json();
            } else {
                // Server returned HTML (500 / 404 error page) — surface a readable message
                const status = resp.status;
                setFormErrors([
                    `Server error (HTTP ${status}). ` +
                    (status === 500
                        ? 'An internal server error occurred. Check that all required packages are installed and the server is running correctly.'
                        : `Unexpected response from server.`)
                ]);
                return;
            }
            if (!resp.ok) {
                setFormErrors(data.errors || [data.error || 'Save failed.']);
                return;
            }
            closeForm();
            await fetchEndpoints();
            
        } catch (e) {
            setFormErrors([`Request failed: ${e.message}`]);
        } finally {
            setSaving(false);
        }
    }

    // ── Toggle active ────────────────────────────────────────────────────────

    async function _doToggleActive(ep) {
        try {
            const resp = await apiHelper.apiCall(
                `/arcee/webhooks/${ep.id}/`,
                {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ application_id: parseInt(applicationId, 10), is_active: !ep.is_active }),
                }
            );
            if (resp.ok) fetchEndpoints();
        } catch (e) { /* silent */ }
    }

    function toggleActive(ep) {
        const activating = !ep.is_active;
        if (activating && ep.needs_configuration && ep.from_pack) {
            setConfirmingToggleId(ep.id);
        } else {
            _doToggleActive(ep);
        }
    }

    // ── Delete ───────────────────────────────────────────────────────────────

    function handleDelete(ep) {
        showAlertDialog({
            title: 'Delete Webhook Endpoint',
            message: `Delete "${ep.name}"? Any event triggers using this endpoint will need to be updated.`,
            type: 'warning',
            okLabel: 'Delete',
            cancelLabel: 'Cancel',
            showCancel: true,
            onOk: async () => {
                const resp = await apiHelper.apiCall(
                    `/arcee/webhooks/${ep.id}/?application_id=${applicationId}`,
                    { method: 'DELETE' }
                );
                if (resp.ok) fetchEndpoints();
            },
            onCancel: () => { },
        });
    }

    // ── Regenerate signing secret ─────────────────────────────────────────────

    async function handleRegenerateSecret(ep, onSuccess) {
        showAlertDialog({
            title: 'Regenerate Signing Secret',
            message: `This will invalidate the current signing secret for "${ep.name}". Any system already using the old secret will stop verifying webhooks. Are you sure?`,
            type: 'warning',
            okLabel: 'Regenerate',
            cancelLabel: 'Cancel',
            showCancel: true,
            onOk: async () => {
                const resp = await apiHelper.apiCall(
                    `/arcee/webhooks/${ep.id}/regenerate-secret/`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ application_id: parseInt(applicationId, 10) }),
                    }
                );
                if (resp.ok) {
                    const data = await resp.json();
                    fetchEndpoints();
                    // Tell the card to show the new secret inline (same as Reveal)
                    if (onSuccess) onSuccess(data.signing_secret);
                }
            },
            onCancel: () => { },
        });
    }

    // ── Test ─────────────────────────────────────────────────────────────────

    async function handleTest(ep) {
        setTestingId(ep.id);
        setTestResult(null);
        setTestLoading(true);
        try {
            const resp = await apiHelper.apiCall(
                `/arcee/webhooks/${ep.id}/test/`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ application_id: parseInt(applicationId, 10) }),
                }
            );
            const data = await resp.json();
            setTestResult({ endpointId: ep.id, success: data.success, log: data.log, error: data.error });
        } catch (e) {
            setTestResult({ endpointId: ep.id, success: false, error: e.message });
        } finally {
            setTestLoading(false);
        }
    }

    // ── Logs ─────────────────────────────────────────────────────────────────

    async function openLogs(ep, page = 1) {
        setLogsEndpoint(ep);
        setLogsPage(page);
        setLogsLoading(true);
        setLogs([]);
        try {
            const resp = await apiHelper.apiCall(
                `/arcee/webhooks/${ep.id}/logs/?application_id=${applicationId}&page=${page}&page_size=${LOGS_PAGE_SIZE}`,
                { method: 'GET' }
            );
            const data = await resp.json();
            if (resp.ok) {
                setLogs(data.results || data);
                setLogsTotal(data.count || (data.results || data).length);
                setLogsTotalPages(data.total_pages || Math.ceil((data.count || (data.results || data).length) / LOGS_PAGE_SIZE));
            }
        } catch (e) { /* silent */ } finally {
            setLogsLoading(false);
        }
    }

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <div className="d-flex flex-column w-100" style={{ background: '#f8fafc', minHeight: '100vh' }}>

            {/* ── Header ── */}
            <div className="w-100 px-3 py-3 px-md-4 bg-white border-bottom d-flex align-items-center justify-content-between gap-3 shadow-sm" style={{ zIndex: 10 }}>
                <div className="d-flex align-items-center gap-3">
                    <button
                        className="btn btn-light rounded-circle shadow-sm"
                        onClick={() => navigate(`/dashboard/applications/${applicationId}`)}
                        style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        <i className="bi bi-arrow-left"></i>
                    </button>
                    <div>
                        <h4 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                            <i className="bi bi-send-check-fill" style={{ color: theme.primary }}></i>
                            Webhook Endpoints
                        </h4>
                        <small className="text-muted d-none d-md-block">
                            Configure outbound HTTP endpoints fired by event triggers.
                        </small>
                    </div>
                </div>
                <button
                    className="btn fw-semibold d-flex align-items-center gap-2"
                    onClick={openCreateForm}
                    style={{ background: theme.primary, color: '#fff', borderRadius: theme.buttonRadius || '8px', border: 'none' }}
                >
                    <i className="bi bi-plus-lg"></i>
                    <span className="d-none d-sm-inline">New Endpoint</span>
                </button>
            </div>

            <div className="container-fluid p-3 p-md-4" style={{ maxWidth: 1200 }}>

                {/* ── Info Banner ── */}
                <div className="alert d-flex align-items-start gap-3 mb-4 border-0 shadow-sm"
                    style={{ background: theme.primary + '10', borderLeft: `4px solid ${theme.primary}`, borderRadius: theme.cardRadius }}>
                    <i className="bi bi-info-circle-fill mt-1" style={{ color: theme.primary, fontSize: 18 }}></i>
                    <div style={{ fontSize: 13 }}>
                        <strong>How webhook endpoints work:</strong> Create an endpoint (URL + auth) here, then attach it to any
                        Event Trigger via the <strong>Call Webhook</strong> action. When the trigger fires, Arcee sends your event data to
                        that URL automatically — no code needed on your side.
                    </div>
                </div>

                {/* ── Loading / Error / Empty ── */}
                {loading && (
                    <div className="text-center py-5">
                        <div className="spinner-border" style={{ color: theme.primary }} role="status" />
                        <p className="text-muted mt-3">Loading endpoints…</p>
                    </div>
                )}

                {!loading && error && (
                    <div className="alert alert-danger">{error}</div>
                )}

                {!loading && !error && endpoints.length === 0 && (
                    <EmptyState theme={theme} onAdd={openCreateForm} />
                )}

                {/* ── Endpoint Cards ── */}
                {!loading && !error && endpoints.length > 0 && (
                    <div className="d-flex flex-column gap-3">
                        {endpoints.map(ep => (
                            <EndpointCard
                                key={ep.id}
                                ep={ep}
                                theme={theme}
                                applicationId={applicationId}
                                testingId={testingId}
                                testLoading={testLoading}
                                testResult={testResult}
                                confirmingToggle={confirmingToggleId === ep.id}
                                onEdit={() => openEditForm(ep)}
                                onDelete={() => handleDelete(ep)}
                                onTest={() => handleTest(ep)}
                                onToggleActive={() => toggleActive(ep)}
                                onConfirmToggle={() => { setConfirmingToggleId(null); _doToggleActive(ep); }}
                                onCancelToggle={() => setConfirmingToggleId(null)}
                                onViewLogs={() => openLogs(ep)}
                                onCloseTest={() => { setTestResult(null); setTestingId(null); }}
                                onRegenerateSecret={(onSuccess) => handleRegenerateSecret(ep, onSuccess)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* ── Create / Edit Modal ── */}
            {showForm && (
                <EndpointFormModal
                    theme={theme}
                    isEdit={!!editingEndpoint}
                    editingEndpoint={editingEndpoint}
                    applicationId={applicationId}
                    formData={formData}
                    authValues={authValues}
                    formErrors={formErrors}
                    saving={saving}
                    onFieldChange={handleFieldChange}
                    onAuthChange={handleAuthChange}
                    onAddHeader={addHeader}
                    onRemoveHeader={removeHeader}
                    onUpdateHeader={updateHeader}
                    onSave={handleSave}
                    onClose={closeForm}
                />
            )}

            {/* ── Logs Modal ── */}
            {logsEndpoint && (
                <LogsModal
                    theme={theme}
                    endpoint={logsEndpoint}
                    logs={logs}
                    loading={logsLoading}
                    page={logsPage}
                    totalPages={logsTotalPages}
                    total={logsTotal}
                    onPageChange={p => openLogs(logsEndpoint, p)}
                    onClose={() => setLogsEndpoint(null)}
                />
            )}
        </div>
    );
}

// ─── EndpointCard ─────────────────────────────────────────────────────────────

function EndpointCard({ ep, theme, applicationId, testingId, testLoading, testResult, confirmingToggle, onEdit, onDelete, onTest, onToggleActive, onConfirmToggle, onCancelToggle, onViewLogs, onCloseTest, onRegenerateSecret }) {
    const methodStyle = METHOD_COLORS[ep.http_method] || { bg: '#f1f5f9', color: '#475569' };
    const authType = AUTH_TYPES.find(a => a.value === ep.auth_type);
    const isThisTestResult = testResult && testResult.endpointId === ep.id;
    const isTestingThis = testingId === ep.id && testLoading;
    const [revealedSecret, setRevealedSecret] = useState(null);  // full secret fetched on demand
    const [revealLoading, setRevealLoading] = useState(false);

    return (
        <div className="bg-white shadow-sm border" style={{ borderRadius: theme.cardRadius, overflow: 'hidden' }}>
            {/* Card Header */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-4 py-3 border-bottom" style={{ background: '#fafafa' }}>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                    {/* Active toggle */}
                    <div
                        onClick={onToggleActive}
                        title={ep.is_active ? 'Active — click to deactivate' : 'Inactive — click to activate'}
                        style={{
                            width: 40, height: 22, borderRadius: 11, cursor: 'pointer',
                            background: ep.is_active ? theme.primary : '#cbd5e1',
                            position: 'relative', flexShrink: 0, transition: 'background 0.2s'
                        }}
                    >
                        <div style={{
                            position: 'absolute', top: 3, left: ep.is_active ? 21 : 3,
                            width: 16, height: 16, borderRadius: '50%', background: '#fff',
                            transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                        }} />
                    </div>

                    <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: 15 }}>{ep.name}</h6>

                    {/* Method badge */}
                    <span className="fw-bold" style={{
                        ...methodStyle, fontSize: 11, padding: '2px 10px',
                        borderRadius: 30, fontWeight: 700, letterSpacing: 0.5
                    }}>
                        {ep.http_method}
                    </span>

                    {/* Auth badge */}
                    {authType && authType.value !== 'none' && (
                        <span className="d-flex align-items-center gap-1" style={{
                            background: '#f1f5f9', color: '#475569', fontSize: 11,
                            padding: '2px 10px', borderRadius: 30, fontWeight: 600
                        }}>
                            <i className={`bi ${authType.icon}`}></i> {authType.label}
                        </span>
                    )}

                    {!ep.is_active && (
                        <span style={{ background: '#f1f5f9', color: '#94a3b8', fontSize: 11, padding: '2px 10px', borderRadius: 30, fontWeight: 600 }}>
                            Inactive
                        </span>
                    )}

                    {ep.needs_configuration && (
                        <span
                            title="This endpoint was installed from a package. Placeholder credentials must be replaced with real values before it can fire."
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                background: '#fffbeb', color: '#b45309', fontSize: 11,
                                padding: '2px 10px', borderRadius: 30, fontWeight: 600,
                                border: '1px solid #fcd34d',
                            }}
                        >
                            <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: 10 }}></i>
                            Setup Required
                        </span>
                    )}

                    {ep.jolt_enabled && (
                        <span
                            title="Payload Transform (Jolt) is active — outgoing payload is transformed before delivery."
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                background: '#ede9fe', color: '#6d28d9', fontSize: 11,
                                padding: '2px 10px', borderRadius: 30, fontWeight: 600,
                                border: '1px solid #c4b5fd',
                            }}
                        >
                            <i className="bi bi-shuffle" style={{ fontSize: 10 }}></i>
                            Jolt Transform
                        </span>
                    )}
                </div>

                {/* Actions */}
                <div className="d-flex align-items-center gap-2">
                    <button className="btn btn-sm btn-light border" onClick={onViewLogs} title="View delivery logs">
                        <i className="bi bi-journal-text me-1"></i>
                        <span className="d-none d-md-inline">Logs</span>
                    </button>
                    <button className="btn btn-sm btn-light border" onClick={onEdit} title="Edit endpoint">
                        <i className="bi bi-pencil me-1"></i>
                        <span className="d-none d-md-inline">Edit</span>
                    </button>
                    <button
                        className="btn btn-sm fw-semibold"
                        onClick={onTest}
                        disabled={isTestingThis || !ep.is_active}
                        title={!ep.is_active ? 'Endpoint is inactive' : 'Fire a test request now'}
                        style={{ background: theme.primary, color: '#fff', border: 'none' }}
                    >
                        {isTestingThis
                            ? <><span className="spinner-border spinner-border-sm me-1" />Testing…</>
                            : <><i className="bi bi-play-circle me-1"></i><span className="d-none d-md-inline">Test</span></>
                        }
                    </button>
                    <button className="btn btn-sm btn-light border text-danger" onClick={onDelete} title="Delete endpoint">
                        <i className="bi bi-trash"></i>
                    </button>
                </div>
            </div>

            {/* Inline placeholder credentials warning */}
            {confirmingToggle && (
                <div className="d-flex align-items-center justify-content-between gap-3 px-4 py-2 border-bottom"
                    style={{ background: '#fffbeb', borderLeft: '4px solid #f59e0b' }}>
                    <div className="d-flex align-items-center gap-2" style={{ fontSize: 13, color: '#92400e' }}>
                        <i className="bi bi-exclamation-triangle-fill" style={{ color: '#f59e0b', fontSize: 15, flexShrink: 0 }} />
                        <span>
                            <strong>Placeholder credentials detected.</strong> This webhook will fire but fail at the target server until real credentials are set. Activate anyway?
                        </span>
                    </div>
                    <div className="d-flex gap-2 flex-shrink-0">
                        <button
                            className="btn btn-sm fw-semibold"
                            onClick={onConfirmToggle}
                            style={{ background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 14px' }}
                        >
                            Activate
                        </button>
                        <button
                            className="btn btn-sm btn-light border"
                            onClick={onCancelToggle}
                            style={{ borderRadius: 6, padding: '4px 14px', fontSize: 13 }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* URL row */}
            <div className="px-4 py-3 d-flex align-items-center gap-3 border-bottom">
                <i className="bi bi-link-45deg text-muted fs-5"></i>
                <code className="text-break small" style={{ color: '#334155', background: 'transparent', wordBreak: 'break-all' }}>
                    {ep.url}
                </code>
                <div className="ms-auto d-flex gap-3 text-muted small flex-shrink-0">
                    <span title="Timeout"><i className="bi bi-clock me-1"></i>{ep.timeout_seconds}s</span>
                    <span title="Retry count"><i className="bi bi-arrow-repeat me-1"></i>{ep.retry_count}x</span>
                </div>
            </div>

            {/* Payload Signing row */}
            <div className="px-4 py-2 border-bottom" style={{ background: '#f8fafc', fontSize: 12 }}>
                <div className="d-flex align-items-center gap-3">Disabled{ep.signing_enabled}
                    <i className="bi bi-shield-check" style={{ color: theme.primary, fontSize: 14 }}></i>
                    <span className="fw-semibold text-muted">Signing Secret</span>
                    {!revealedSecret ? (
                        <code className="text-muted" style={{ letterSpacing: 1 }}>
                            {ep.signing_enabled ? (ep.signing_secret_preview || '—') : 'Disabled'}
                        </code>
                    ) : (
                        <code style={{ color: '#334155', letterSpacing: 0.5, wordBreak: 'break-all' }}>
                            {revealedSecret}
                        </code>
                    )}
                    <div className="ms-auto d-flex gap-2 flex-shrink-0">
                        {ep.signing_enabled && (
                            <>
                                {/* Reveal / Hide */}
                                <button
                                    className="btn btn-sm btn-light border"
                                    style={{ fontSize: 11 }}
                                    disabled={revealLoading}
                                    title={revealedSecret ? 'Hide secret' : 'Reveal full secret'}
                                    onClick={async () => {
                                        if (revealedSecret) { setRevealedSecret(null); return; }
                                        setRevealLoading(true);
                                        try {
                                            const resp = await apiHelper.apiCall(
                                                `/arcee/webhooks/${ep.id}/reveal-secret/?application_id=${ep.application_id || applicationId}`,
                                                { method: 'GET' }
                                            );
                                            const d = await resp.json();
                                            if (resp.ok) setRevealedSecret(d.signing_secret);
                                        } finally { setRevealLoading(false); }
                                    }}
                                >
                                    {revealLoading
                                        ? <span className="spinner-border spinner-border-sm" />
                                        : <><i className={`bi ${revealedSecret ? 'bi-eye-slash' : 'bi-eye'} me-1`}></i>{revealedSecret ? 'Hide' : 'Reveal'}</>
                                    }
                                </button>
                                {/* Copy */}
                                {revealedSecret && (
                                    <button
                                        className="btn btn-sm btn-light border"
                                        style={{ fontSize: 11 }}
                                        title="Copy to clipboard"
                                        onClick={() => navigator.clipboard.writeText(revealedSecret)}
                                    >
                                        <i className="bi bi-clipboard me-1"></i>Copy
                                    </button>
                                )}
                                {/* Regenerate — on success, shows new secret inline (same as Reveal) */}
                                <button
                                    className="btn btn-sm btn-light border"
                                    style={{ fontSize: 11 }}
                                    onClick={() => {
                                        setRevealedSecret(null);
                                        onRegenerateSecret((newSec) => setRevealedSecret(newSec));
                                    }}
                                    title="Generate a new signing secret (invalidates the old one)"
                                >
                                    <i className="bi bi-arrow-clockwise me-1"></i>Regenerate
                                </button>
                            </>
                        )}
                    </div>
                </div>
                {revealedSecret && (
                    <div className="mt-1 text-muted" style={{ fontSize: 11 }}>
                        Share this with the receiving system's developer. Keep it secret — treat it like a password.
                    </div>
                )}
            </div>

            {/* Test Result Panel */}
            {isThisTestResult && (
                <TestResultPanel result={testResult} theme={theme} onClose={onCloseTest} />
            )}
        </div>
    );
}

// ─── TestResultPanel ──────────────────────────────────────────────────────────

function TestResultPanel({ result, theme, onClose }) {
    const success = result.success;
    const log = result.log;
    const [showBody, setShowBody] = useState(false);

    return (
        <div className="px-4 py-3" style={{ background: success ? '#f0fdf4' : '#fef2f2', borderTop: `2px solid ${success ? '#86efac' : '#fca5a5'}` }}>
            <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center gap-2 fw-semibold" style={{ color: success ? '#166534' : '#b91c1c' }}>
                    <i className={`bi ${success ? 'bi-check-circle-fill' : 'bi-x-circle-fill'} fs-5`}></i>
                    {success ? 'Test delivered successfully' : 'Test delivery failed'}
                </div>
                <button className="btn btn-sm btn-light border" onClick={onClose}><i className="bi bi-x"></i></button>
            </div>

            {log && (
                <div className="d-flex flex-wrap gap-3 mb-2" style={{ fontSize: 12 }}>
                    {log.response_status && (
                        <span className="fw-bold" style={{ color: success ? '#166534' : '#b91c1c' }}>
                            HTTP {log.response_status}
                        </span>
                    )}
                    {log.response_time_ms != null && (
                        <span className="text-muted"><i className="bi bi-clock me-1"></i>{log.response_time_ms}ms</span>
                    )}
                    {log.attempt_number && (
                        <span className="text-muted">Attempt #{log.attempt_number}</span>
                    )}
                </div>
            )}

            {!success && (result.error || (log && log.error_message)) && (
                <div className="p-2 rounded small" style={{ background: '#fee2e2', color: '#7f1d1d', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {result.error || log.error_message}
                </div>
            )}

            {log && log.response_body && (
                <div className="mt-2">
                    <button className="btn btn-sm btn-link p-0 text-muted" onClick={() => setShowBody(s => !s)} style={{ fontSize: 12 }}>
                        {showBody ? 'Hide' : 'Show'} response body
                    </button>
                    {showBody && (
                        <pre className="mt-1 p-2 rounded small" style={{ background: '#1e293b', color: '#94a3b8', maxHeight: 200, overflow: 'auto', marginBottom: 0 }}>
                            {log.response_body}
                        </pre>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── EndpointFormModal ────────────────────────────────────────────────────────

function EndpointFormModal({ theme, isEdit, editingEndpoint, applicationId, formData, authValues, formErrors, saving, onFieldChange, onAuthChange, onAddHeader, onRemoveHeader, onUpdateHeader, onSave, onClose }) {
    const [showActiveWarning, setShowActiveWarning] = useState(false);

    // Jolt preview state (available in both create and edit mode)
    const [joltPreview, setJoltPreview] = useState(null);          // {success, result, warning, error}
    const [joltPreviewLoading, setJoltPreviewLoading] = useState(false);
    const [joltPreviewEventType, setJoltPreviewEventType] = useState('record.created');

    async function handleJoltPreview() {
        setJoltPreviewLoading(true);
        setJoltPreview(null);
        try {
            let spec;
            try {
                spec = JSON.parse(formData.jolt_spec);
            } catch (e) {
                setJoltPreview({ success: false, warning: null, error: 'Jolt Spec is not valid JSON: ' + e.message });
                return;
            }
            // Edit mode → endpoint-scoped URL; Create mode → stateless URL
            const url = isEdit && editingEndpoint?.id
                ? `/arcee/webhooks/${editingEndpoint.id}/transform-preview/`
                : `/arcee/webhooks/transform-preview/`;
            const resp = await apiHelper.apiCall(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    application_id: parseInt(applicationId, 10),
                    spec,
                    event_type: joltPreviewEventType,
                }),
            });
            const contentType = resp.headers.get('content-type') || '';
            if (!contentType.includes('application/json')) {
                const httpStatus = resp.status;
                setJoltPreview({
                    success: false,
                    warning: null,
                    error: `Server error (HTTP ${httpStatus}). ` +
                        (httpStatus === 500
                            ? 'Internal server error — ensure jolt-py is installed (pip install jolt-py) and the server has been restarted.'
                            : 'Unexpected response from server.'),
                });
                return;
            }
            const data = await resp.json();
            setJoltPreview(data);
        } catch (e) {
            setJoltPreview({ success: false, warning: null, error: `Request failed: ${e.message}` });
        } finally {
            setJoltPreviewLoading(false);
        }
    }

    function handleActiveToggle() {
        const activating = !formData.is_active;
        if (activating && editingEndpoint?.needs_configuration && editingEndpoint?.from_pack) {
            setShowActiveWarning(true);
        } else {
            setShowActiveWarning(false);
            onFieldChange('is_active', !formData.is_active);
        }
    }
    const authFields = getAuthFields(formData.auth_type);
    // Tracks which password fields are currently shown as plain text
    const [revealedFields, setRevealedFields] = useState({});
    function toggleReveal(key) {
        setRevealedFields(prev => ({ ...prev, [key]: !prev[key] }));
    }

    return (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1050 }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable" style={{ maxWidth: 700 }}>
                <div className="modal-content" style={{ borderRadius: theme.cardRadius }}>

                    {/* Header */}
                    <div className="modal-header border-0 pb-0">
                        <div>
                            <h5 className="modal-title fw-bold">
                                <i className={`bi ${isEdit ? 'bi-pencil-square' : 'bi-plus-circle'} me-2`} style={{ color: theme.primary }}></i>
                                {isEdit ? 'Edit Webhook Endpoint' : 'New Webhook Endpoint'}
                            </h5>
                            <p className="text-muted small mb-0">Configure where Arcee sends event data.</p>
                        </div>
                        <button className="btn-close" onClick={onClose} />
                    </div>

                    {/* Body */}
                    <div className="modal-body pt-3">

                        {formErrors.length > 0 && (
                            <div className="alert alert-danger py-2">
                                <ul className="mb-0 ps-3">
                                    {formErrors.map((e, i) => <li key={i}>{e}</li>)}
                                </ul>
                            </div>
                        )}

                        {/* ── Basic Info ── */}
                        <SectionLabel label="Basic Configuration" />

                        <div className="row g-3 mb-3">
                            <div className="col-12">
                                <label className="form-label fw-semibold small">Endpoint Name <span className="text-danger">*</span></label>
                                <input
                                    className="form-control"
                                    placeholder="e.g. Notify CRM on Record Created"
                                    value={formData.name}
                                    maxLength={60}
                                    onChange={e => onFieldChange('name', e.target.value.replace(/[^A-Za-z0-9 _.()-]/g, ''))}
                                    style={{ borderRadius: theme.buttonRadius }}
                                />
                                <div className="form-text">A descriptive name you'll pick from when building event triggers.</div>
                            </div>

                            <div className="col-12">
                                <label className="form-label fw-semibold small">Target URL <span className="text-danger">*</span></label>
                                <input
                                    className="form-control"
                                    type="url"
                                    placeholder="https://example.com/hooks/arcee"
                                    value={formData.url}
                                    maxLength={2048}
                                    onChange={e => onFieldChange('url', e.target.value.replace(/\s/g, ''))}
                                    style={{ borderRadius: theme.buttonRadius }}
                                />
                            </div>

                            <div className="col-3">
                                <label className="form-label fw-semibold small">HTTP Method</label>
                                <select className="form-select" value={formData.http_method} onChange={e => {
                                    const method = e.target.value;
                                    onFieldChange('http_method', method);
                                    if (method === 'GET') onFieldChange('body_format', 'query_params');
                                    else if (formData.body_format === 'query_params') onFieldChange('body_format', 'json');
                                }} style={{ borderRadius: theme.buttonRadius }}>
                                    {HTTP_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                                </select>
                            </div>

                            <div className="col-3">
                                <label className="form-label fw-semibold small">Body Format</label>
                                <select
                                    className="form-select"
                                    value={formData.body_format}
                                    onChange={e => onFieldChange('body_format', e.target.value)}
                                    disabled={formData.http_method === 'GET'}
                                    style={{ borderRadius: theme.buttonRadius }}
                                >
                                    {BODY_FORMATS.map(f => {
                                        const isGetOnly = f.value === 'query_params';
                                        const isBodyOnly = f.value !== 'query_params';
                                        const disabled = (isGetOnly && formData.http_method !== 'GET') ||
                                                         (isBodyOnly && formData.http_method === 'GET');
                                        return <option key={f.value} value={f.value} disabled={disabled}>{f.label}</option>;
                                    })}
                                </select>
                                <div className="form-text text-muted" style={{ fontSize: '11px' }}>
                                    {BODY_FORMATS.find(f => f.value === formData.body_format)?.description}
                                </div>
                            </div>

                            <div className="col-3">
                                <label className="form-label fw-semibold small">Timeout (s)</label>
                                <input
                                    className="form-control"
                                    type="text"
                                    value={formData.timeout_seconds}
                                    onChange={(e) => {
                                        let value = e.target.value.replace(/\D/g, ""); // only digits

                                        if (value === "") {
                                        onFieldChange("timeout_seconds", "");
                                        return;
                                        }

                                        let num = parseInt(value, 10);

                                        // prevent exceeding max while typing
                                        if (num > 120) return;

                                        onFieldChange("timeout_seconds", num);
                                    }}
                                    onBlur={() => {
                                        const num = parseInt(formData.timeout_seconds, 10);
                                        onFieldChange(
                                        "timeout_seconds",
                                        isNaN(num) ? 30 : Math.max(5, num)
                                        );
                                    }}
                                    style={{ borderRadius: theme.buttonRadius }}
                                />
                            </div>

                            <div className="col-3">
                                <label className="form-label fw-semibold small">Retries</label>
                                <input
                                    className="form-control"
                                    type="text"
                                    value={formData.retry_count}
                                    onChange={(e) => {
                                        let value = e.target.value.replace(/\D/g, "");

                                        if (value === "") {
                                            onFieldChange("retry_count", "");
                                            return;
                                        }

                                        let num = parseInt(value, 10);

                                        // prevent exceeding max while typing
                                        if (num > 5) return;

                                        onFieldChange("retry_count", num);
                                    }}
                                    onBlur={() => {
                                        const num = parseInt(formData.retry_count, 10);
                                        onFieldChange(
                                            "retry_count",
                                            isNaN(num) ? 0 : Math.max(0, num)
                                        );
                                    }}
                                    style={{ borderRadius: theme.buttonRadius }}
                                />
                            </div>

                            <div className="col-12">
                                <label className="form-label fw-semibold small d-flex align-items-center gap-2">
                                    <div
                                        onClick={handleActiveToggle}
                                        style={{
                                            width: 40, height: 22, borderRadius: 11, cursor: 'pointer',
                                            background: formData.is_active ? theme.primary : '#cbd5e1',
                                            position: 'relative', flexShrink: 0, transition: 'background 0.2s'
                                        }}
                                    >
                                        <div style={{
                                            position: 'absolute', top: 3, left: formData.is_active ? 21 : 3,
                                            width: 16, height: 16, borderRadius: '50%', background: '#fff',
                                            transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                        }} />
                                    </div>
                                    Active
                                </label>
                                <div className="form-text">Inactive endpoints are skipped by event triggers.</div>

                                {showActiveWarning && (
                                    <div className="d-flex align-items-center justify-content-between gap-3 mt-2 px-3 py-2 rounded"
                                        style={{ background: '#fffbeb', border: '1px solid #f59e0b' }}>
                                        <div className="d-flex align-items-center gap-2" style={{ fontSize: 12, color: '#92400e' }}>
                                            <i className="bi bi-exclamation-triangle-fill" style={{ color: '#f59e0b', flexShrink: 0 }} />
                                            <span><strong>Placeholder credentials detected.</strong> Webhook will fail until real credentials are set.</span>
                                        </div>
                                        <div className="d-flex gap-2 flex-shrink-0">
                                            <button
                                                type="button"
                                                className="btn btn-sm fw-semibold"
                                                onClick={() => { setShowActiveWarning(false); onFieldChange('is_active', true); }}
                                                style={{ background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6, padding: '3px 12px', fontSize: 12 }}
                                            >
                                                Activate
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-light border"
                                                onClick={() => setShowActiveWarning(false)}
                                                style={{ borderRadius: 6, padding: '3px 12px', fontSize: 12 }}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ── Signing Secret toggle ── */}
                        <div className="col-12 mb-3">
                            <label className="form-label fw-semibold small d-flex align-items-center gap-2">
                                <div
                                    onClick={() => onFieldChange('enable_signing', !formData.enable_signing)}
                                    style={{
                                        width: 40, height: 22, borderRadius: 11, cursor: 'pointer',
                                        background: formData.enable_signing ? theme.primary : '#cbd5e1',
                                        position: 'relative', flexShrink: 0, transition: 'background 0.2s'
                                    }}
                                >
                                    <div style={{
                                        position: 'absolute', top: 3, left: formData.enable_signing ? 21 : 3,
                                        width: 16, height: 16, borderRadius: '50%', background: '#fff',
                                        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                    }} />
                                </div>
                                Enable Payload Signing (X-Arcee-Signature)
                            </label>
                            <div className="form-text">
                                {formData.enable_signing
                                    ? 'A signing secret will be generated. Share it with the receiver to verify payload integrity.'
                                    : 'Disabled. Recommended for No Auth endpoints where there is no other way to verify the request came from Arcee.'}
                            </div>
                        </div>

                        {/* ── Meta Block Toggle ── */}
                        <div className="col-12 mb-3">
                            <label className="form-label fw-semibold small d-flex align-items-center gap-2">
                                <div
                                    onClick={() => onFieldChange('include_meta', !formData.include_meta)}
                                    style={{
                                        width: 40, height: 22, borderRadius: 11, cursor: 'pointer',
                                        background: formData.include_meta ? theme.primary : '#cbd5e1',
                                        position: 'relative', flexShrink: 0, transition: 'background 0.2s'
                                    }}
                                >
                                    <div style={{
                                        position: 'absolute', top: 3, left: formData.include_meta ? 21 : 3,
                                        width: 16, height: 16, borderRadius: '50%', background: '#fff',
                                        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                    }} />
                                </div>
                                Include Meta Block
                            </label>
                            <div className="form-text">
                                {formData.include_meta ? (
                                    <>
                                        <span className="text-success fw-semibold"><i className="bi bi-check-circle me-1"></i>On — </span>
                                        A <code>meta</code> object will be appended to the payload with routing info:{' '}
                                        <code>delivery_id</code>, <code>timestamp</code>, <code>org_id</code>, <code>app_id</code>,{' '}
                                        <code>table</code>, <code>record_ids</code>, and <code>changes</code> (on updates).
                                    </>
                                ) : (
                                    <>
                                        <span className="text-muted fw-semibold"><i className="bi bi-dash-circle me-1"></i>Off — </span>
                                        Only flat record fields + <code>event</code> key are sent. No routing info.
                                    </>
                                )}
                            </div>
                        </div>

                        {/* ── Authentication ── */}
                        <SectionLabel label="Authentication" />

                        <div className="mb-3">
                            <label className="form-label fw-semibold small">Auth Type</label>
                            <div className="d-flex flex-wrap gap-2">
                                {AUTH_TYPES.map(a => (
                                    <button
                                        key={a.value}
                                        type="button"
                                        onClick={() => onFieldChange('auth_type', a.value)}
                                        className="btn btn-sm d-flex align-items-center gap-1"
                                        style={{
                                            border: `1px solid ${formData.auth_type === a.value ? theme.primary : '#e2e8f0'}`,
                                            background: formData.auth_type === a.value ? theme.primary + '15' : '#fff',
                                            color: formData.auth_type === a.value ? theme.primary : '#475569',
                                            fontWeight: formData.auth_type === a.value ? 700 : 400,
                                            borderRadius: theme.buttonRadius,
                                        }}
                                    >
                                        <i className={`bi ${a.icon}`}></i> {a.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {authFields.length > 0 && (
                            <div className="row g-3 mb-3 p-3 rounded" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                {authFields.map(f => {
                                    if (f.type === 'select') {
                                        return (
                                            <div className="col-12 col-md-6" key={f.key}>
                                                <label className="form-label fw-semibold small">{f.label}</label>
                                                <select className="form-select" value={authValues[f.key] || f.options[0].value}
                                                    onChange={e => onAuthChange(f.key, e.target.value)} style={{ borderRadius: theme.buttonRadius }}>
                                                    {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                </select>
                                            </div>
                                        );
                                    }
                                    const isPassword = f.type === 'password';
                                    const revealed = revealedFields[f.key];
                                    const alreadySaved = isPassword && isEdit && editingEndpoint?.auth_config?.[f.key] === '***';
                                    const inputPlaceholder = alreadySaved ? 'Saved — leave blank to keep, or type to replace' : f.placeholder;
                                    return (
                                        <div className="col-12 col-md-6" key={f.key}>
                                            <label className="form-label fw-semibold small">
                                                {f.label} {f.required && !alreadySaved && <span className="text-danger">*</span>}
                                                {alreadySaved && <span className="text-success ms-1" style={{fontSize:'11px'}}><i className="bi bi-check-circle me-1"></i>Saved</span>}
                                            </label>
                                            <div className={isPassword ? 'input-group' : undefined}>
                                                <input
                                                    className="form-control"
                                                    type={isPassword && !revealed ? 'password' : 'text'}
                                                    placeholder={inputPlaceholder}
                                                    value={authValues[f.key] || ''}
                                                    maxLength={f.maxLength}
                                                    onChange={e => {
                                                        let val = e.target.value;
                                                        // API key value: strip spaces
                                                        if (f.key === 'value') {
                                                            val = val.replace(/\s/g, '');
                                                        }
                                                        // JWT expiry: digits only
                                                        if (f.key === 'expiry_seconds') {
                                                            val = val.replace(/\D/g, '');
                                                        }
                                                        // Scope: letters and spaces only
                                                        if (f.key === 'scope') {
                                                            val = val.replace(/[^A-Za-z ]/g, '');
                                                        }
                                                        onAuthChange(f.key, val);
                                                    }}
                                                    style={{ borderRadius: isPassword ? `${theme.buttonRadius} 0 0 ${theme.buttonRadius}` : theme.buttonRadius }}
                                                />
                                                {isPassword && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-light border"
                                                        onClick={() => toggleReveal(f.key)}
                                                        title={revealed ? 'Hide' : 'Show'}
                                                        style={{ borderRadius: `0 ${theme.buttonRadius} ${theme.buttonRadius} 0` }}
                                                    >
                                                        <i className={`bi ${revealed ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                {isEdit && (
                                    <div className="col-12">
                                        <div className="form-text text-warning">
                                            <i className="bi bi-exclamation-triangle me-1"></i>
                                            Secret fields are masked. Leave blank to keep existing value, or enter a new one to replace it.
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Custom Headers ── */}
                        <SectionLabel label="Custom Headers (optional)" />
                        <div className="mb-3">
                            {formData.custom_headers.map((h, i) => (
                                <div key={i} className="d-flex gap-2 mb-2">
                                    <input className="form-control form-control-sm" placeholder="Header-Name" value={h.key}
                                        onChange={e => onUpdateHeader(i, 'key', e.target.value)} style={{ borderRadius: theme.buttonRadius }} />
                                    <input className="form-control form-control-sm" placeholder="Value" value={h.value}
                                        onChange={e => onUpdateHeader(i, 'value', e.target.value)} style={{ borderRadius: theme.buttonRadius }} />
                                    <button className="btn btn-sm btn-light border text-danger flex-shrink-0" onClick={() => onRemoveHeader(i)}>
                                        <i className="bi bi-trash"></i>
                                    </button>
                                </div>
                            ))}
                            <button className="btn btn-sm btn-light border" onClick={onAddHeader}>
                                <i className="bi bi-plus-circle me-1"></i> Add Header
                            </button>
                        </div>

                        {/* ── Payload Transform (Jolt) ── */}
                        <SectionLabel label="Payload Transform (Jolt)" />

                        {/* Enable toggle */}
                        <div className="col-12 mb-3">
                            <label className="form-label fw-semibold small d-flex align-items-center gap-2">
                                <div
                                    onClick={() => {
                                        onFieldChange('jolt_enabled', !formData.jolt_enabled);
                                        setJoltPreview(null);
                                    }}
                                    style={{
                                        width: 40, height: 22, borderRadius: 11, cursor: 'pointer',
                                        background: formData.jolt_enabled ? theme.primary : '#cbd5e1',
                                        position: 'relative', flexShrink: 0, transition: 'background 0.2s'
                                    }}
                                >
                                    <div style={{
                                        position: 'absolute', top: 3, left: formData.jolt_enabled ? 21 : 3,
                                        width: 16, height: 16, borderRadius: '50%', background: '#fff',
                                        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                    }} />
                                </div>
                                Enable Payload Transform
                            </label>
                            <div className="form-text">
                                {formData.jolt_enabled
                                    ? 'Outgoing payload will be transformed by your Jolt spec before delivery. Apache NiFi JoltTransformJSON syntax.'
                                    : 'Off — payload is delivered as-is. Enable to reshape the JSON with a Jolt spec.'}
                            </div>
                        </div>

                        {formData.jolt_enabled && (
                            <div className="p-3 rounded mb-3" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>

                                {/* Warn when body format is form_data or method is GET — Jolt nested output becomes dot-notation keys */}
                                {(formData.body_format === 'form_data' || formData.http_method === 'GET') && (
                                    <div className="d-flex align-items-start gap-2 rounded p-2 mb-3" style={{
                                        background: '#fffbeb', border: '1px solid #fcd34d', fontSize: 12,
                                    }}>
                                        <i className="bi bi-exclamation-triangle-fill text-warning mt-1" style={{ flexShrink: 0 }}></i>
                                        <span style={{ color: '#713f12' }}>
                                            Jolt is enabled with <strong>{formData.http_method === 'GET' ? 'GET (query params)' : 'Form Data'}</strong> format.
                                            If your spec produces nested objects (e.g. destination <code>"data.recordId"</code>), they arrive as underscore-separated keys — <code>data_recordId=42</code>, not a nested structure. Keep your spec output flat to avoid unexpected receiver behaviour.
                                        </span>
                                    </div>
                                )}

                                {/* Failure behaviour */}
                                <div className="mb-3">
                                    <label className="form-label fw-semibold small">On Transform Failure</label>
                                    <div className="d-flex gap-3">
                                        {[
                                            { value: 'open',   label: 'Fail-open',   desc: 'Send original payload', icon: 'bi-skip-forward-fill' },
                                            { value: 'closed', label: 'Fail-closed', desc: 'Abort & retry',          icon: 'bi-x-octagon-fill' },
                                        ].map(opt => (
                                            <label
                                                key={opt.value}
                                                className="d-flex align-items-start gap-2 p-2 rounded"
                                                style={{
                                                    cursor: 'pointer', flex: 1,
                                                    border: `1px solid ${formData.jolt_on_failure === opt.value ? theme.primary : '#e2e8f0'}`,
                                                    background: formData.jolt_on_failure === opt.value ? theme.primary + '10' : '#fff',
                                                    borderRadius: theme.buttonRadius,
                                                }}
                                            >
                                                <input
                                                    type="radio"
                                                    name="jolt_on_failure"
                                                    value={opt.value}
                                                    checked={formData.jolt_on_failure === opt.value}
                                                    onChange={() => onFieldChange('jolt_on_failure', opt.value)}
                                                    style={{ marginTop: 3, accentColor: theme.primary }}
                                                />
                                                <div>
                                                    <div className="fw-semibold" style={{ fontSize: 13, color: formData.jolt_on_failure === opt.value ? theme.primary : '#334155' }}>
                                                        <i className={`bi ${opt.icon} me-1`}></i>{opt.label}
                                                    </div>
                                                    <div className="text-muted" style={{ fontSize: 11 }}>{opt.desc}</div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Spec editor */}
                                <div className="mb-3">
                                    <label className="form-label fw-semibold small mb-1">
                                        Jolt Spec <span className="text-danger">*</span>
                                    </label>
                                    <textarea
                                        className="form-control"
                                        rows={8}
                                        value={formData.jolt_spec}
                                        onChange={e => { onFieldChange('jolt_spec', e.target.value); setJoltPreview(null); }}
                                        placeholder={'[\n  {\n    "operation": "shift",\n    "spec": {\n      "event": "eventType",\n      "id": "recordId"\n    }\n  }\n]'}
                                        style={{
                                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                            fontSize: 12,
                                            borderRadius: theme.buttonRadius,
                                            resize: 'vertical',
                                        }}
                                        spellCheck={false}
                                    />
                                    <div className="form-text">
                                        JSON array of operation objects. Supported:{' '}
                                        <code>shift</code>, <code>default</code>, <code>remove</code>,{' '}
                                        <code>sort</code>, <code>cardinality</code>,{' '}
                                        <code>modify-overwrite-beta</code>, <code>modify-default-beta</code>.
                                    </div>
                                </div>

                                {/* Preview controls — event selector + button */}
                                <div className="d-flex align-items-center gap-1 mb-1" style={{ fontSize: 11, color: '#64748b' }}>
                                    <i className="bi bi-info-circle"></i>
                                    <span>Preview uses a built-in sample payload. Results may differ for your actual table data.</span>
                                </div>
                                <div className="d-flex align-items-center gap-2 mb-3">
                                    <label className="fw-semibold text-muted mb-0 text-nowrap" style={{ fontSize: 12 }}>
                                        Preview as:
                                    </label>
                                    <select
                                        className="form-select form-select-sm"
                                        value={joltPreviewEventType}
                                        onChange={e => { setJoltPreviewEventType(e.target.value); setJoltPreview(null); }}
                                        style={{ fontSize: 12, maxWidth: 220 }}
                                    >
                                        {Object.entries(EVENT_TYPE_GROUPS).map(([group, events]) => (
                                            <optgroup key={group} label={group}>
                                                {events.map(et => (
                                                    <option key={et.value} value={et.value}>{et.label}</option>
                                                ))}
                                            </optgroup>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light border d-flex align-items-center gap-1 ms-auto"
                                        onClick={handleJoltPreview}
                                        disabled={joltPreviewLoading || !formData.jolt_spec.trim()}
                                        style={{ fontSize: 12, whiteSpace: 'nowrap' }}
                                    >
                                        {joltPreviewLoading
                                            ? <><span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} /> Previewing…</>
                                            : <><i className="bi bi-play-circle me-1"></i>Validate &amp; Preview</>
                                        }
                                    </button>
                                </div>

                                {/* Preview result */}
                                {joltPreview && (
                                    <div className="rounded p-3 mb-2" style={{
                                        background: joltPreview.success ? '#f0fdf4' : '#fef2f2',
                                        border: `1px solid ${joltPreview.success ? '#86efac' : '#fca5a5'}`,
                                    }}>
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <span className="fw-semibold d-flex align-items-center gap-2" style={{
                                                color: joltPreview.success ? '#166534' : '#b91c1c', fontSize: 13
                                            }}>
                                                <i className={`bi ${joltPreview.success ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}></i>
                                                {joltPreview.success ? 'Transform succeeded' : 'Transform failed'}
                                            </span>
                                            <button className="btn btn-sm btn-link p-0 text-muted" onClick={() => setJoltPreview(null)}>
                                                <i className="bi bi-x"></i>
                                            </button>
                                        </div>

                                        {/* Warning — empty result {} */}
                                        {joltPreview.warning && (
                                            <div className="d-flex align-items-start gap-2 rounded p-2 mb-2" style={{
                                                background: '#fefce8', border: '1px solid #fde047', fontSize: 11,
                                            }}>
                                                <i className="bi bi-exclamation-triangle-fill text-warning mt-1" style={{ flexShrink: 0 }}></i>
                                                <span style={{ color: '#713f12' }}>{joltPreview.warning}</span>
                                            </div>
                                        )}

                                        {joltPreview.error && (
                                            <pre style={{
                                                margin: 0, padding: '8px 10px', borderRadius: 6,
                                                background: '#fee2e2', color: '#7f1d1d',
                                                fontSize: 11, fontFamily: 'monospace',
                                                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                            }}>
                                                {joltPreview.error}
                                            </pre>
                                        )}
                                        {joltPreview.success && (
                                            <div className="row g-2 mt-1" style={{ fontSize: 11 }}>
                                                <div className="col-6">
                                                    <div className="fw-semibold text-muted mb-1 text-uppercase" style={{ fontSize: 10, letterSpacing: '0.05em' }}>
                                                        Sample Input ({joltPreviewEventType})
                                                    </div>
                                                    <pre style={{
                                                        margin: 0, padding: '8px 10px', borderRadius: 6,
                                                        background: '#1e293b', color: '#94a3b8',
                                                        maxHeight: 200, overflow: 'auto',
                                                        fontSize: 11, fontFamily: 'monospace',
                                                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                                    }}>
                                                        {JSON.stringify(joltPreview.sample_input || {}, null, 2)}
                                                    </pre>
                                                </div>
                                                <div className="col-6">
                                                    <div className="fw-semibold text-muted mb-1 text-uppercase" style={{ fontSize: 10, letterSpacing: '0.05em' }}>
                                                        Transformed Output
                                                    </div>
                                                    <pre style={{
                                                        margin: 0, padding: '8px 10px', borderRadius: 6,
                                                        background: joltPreview.warning ? '#fefce8' : '#f0fdf4',
                                                        color: joltPreview.warning ? '#713f12' : '#166534',
                                                        maxHeight: 200, overflow: 'auto',
                                                        fontSize: 11, fontFamily: 'monospace',
                                                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                                    }}>
                                                        {JSON.stringify(joltPreview.result, null, 2)}
                                                    </pre>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="modal-footer border-0 pt-0">
                        <button className="btn btn-light border" onClick={onClose} disabled={saving}>Cancel</button>
                        <button
                            className="btn fw-semibold"
                            onClick={onSave}
                            disabled={saving}
                            style={{ background: theme.primary, color: '#fff', border: 'none', borderRadius: theme.buttonRadius }}
                        >
                            {saving
                                ? <><span className="spinner-border spinner-border-sm me-2" />{isEdit ? 'Saving…' : 'Creating…'}</>
                                : <><i className={`bi ${isEdit ? 'bi-check-lg' : 'bi-plus-circle'} me-2`}></i>{isEdit ? 'Save Changes' : 'Create Endpoint'}</>
                            }
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── LogsModal ────────────────────────────────────────────────────────────────

function LogDetailPanel({ log, onClose }) {
    const hasError    = !!log.error_message;
    const hasResponse = !!log.response_body;

    function copyText(text) {
        navigator.clipboard.writeText(text).catch(() => {});
    }

    return (
        <tr>
            <td colSpan={8} style={{ padding: 0, borderTop: 'none' }}>
                <div style={{
                    margin: '0 16px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    overflow: 'hidden',
                    background: '#fafafa',
                }}>
                    {/* Header bar */}
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '8px 14px',
                        background: hasError ? '#fff5f5' : '#f0fdf4',
                        borderBottom: '1px solid #e5e7eb',
                    }}>
                        <span style={{ fontWeight: 600, fontSize: 12, color: hasError ? '#b91c1c' : '#166534' }}>
                            <i className={`bi ${hasError ? 'bi-exclamation-circle' : 'bi-check-circle'} me-1`}></i>
                            {hasError ? 'Delivery Error' : 'Delivery Successful'}
                        </span>
                        <button
                            className="btn btn-link p-0 text-muted"
                            style={{ fontSize: 11 }}
                            onClick={onClose}
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>

                    <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {/* Request URL */}
                        {log.request_url && (
                            <div>
                                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Request URL
                                </div>
                                <div style={{
                                    background: '#f1f5f9', borderRadius: 6, padding: '6px 10px',
                                    fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all',
                                    color: '#334155',
                                }}>
                                    {log.request_url}
                                </div>
                            </div>
                        )}

                        {/* Error message */}
                        {hasError && (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Error Details
                                    </span>
                                    <button
                                        className="btn btn-link p-0 text-muted"
                                        style={{ fontSize: 11 }}
                                        title="Copy error"
                                        onClick={() => copyText(log.error_message)}
                                    >
                                        <i className="bi bi-clipboard me-1"></i>Copy
                                    </button>
                                </div>
                                <pre style={{
                                    margin: 0,
                                    background: '#fff5f5',
                                    border: '1px solid #fecaca',
                                    borderRadius: 6,
                                    padding: '8px 10px',
                                    fontSize: 12,
                                    color: '#991b1b',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    maxHeight: 160,
                                    overflowY: 'auto',
                                    fontFamily: 'monospace',
                                }}>
                                    {log.error_message}
                                </pre>
                            </div>
                        )}

                        {/* Response body */}
                        {hasResponse && (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Response Body
                                    </span>
                                    <button
                                        className="btn btn-link p-0 text-muted"
                                        style={{ fontSize: 11 }}
                                        title="Copy response"
                                        onClick={() => copyText(log.response_body)}
                                    >
                                        <i className="bi bi-clipboard me-1"></i>Copy
                                    </button>
                                </div>
                                <pre style={{
                                    margin: 0,
                                    background: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 6,
                                    padding: '8px 10px',
                                    fontSize: 12,
                                    color: '#334155',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    maxHeight: 160,
                                    overflowY: 'auto',
                                    fontFamily: 'monospace',
                                }}>
                                    {(() => {
                                        try { return JSON.stringify(JSON.parse(log.response_body), null, 2); }
                                        catch { return log.response_body; }
                                    })()}
                                </pre>
                            </div>
                        )}

                        {/* Nothing to show */}
                        {!hasError && !hasResponse && !log.request_url && (
                            <span className="text-muted small">No additional details available.</span>
                        )}
                    </div>
                </div>
            </td>
        </tr>
    );
}

function LogsModal({ theme, endpoint, logs, loading, page, totalPages, total, onPageChange, onClose }) {
    const [expandedId, setExpandedId] = useState(null);

    function toggleRow(id) {
        setExpandedId(prev => (prev === id ? null : id));
    }

    return (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1050 }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
                <div className="modal-content" style={{ borderRadius: theme.cardRadius }}>
                    <div className="modal-header border-0">
                        <div>
                            <h5 className="modal-title fw-bold">
                                <i className="bi bi-journal-text me-2" style={{ color: theme.primary }}></i>
                                Delivery Logs — {endpoint.name}
                            </h5>
                            <p className="text-muted small mb-0">{total} total deliveries</p>
                        </div>
                        <button className="btn-close" onClick={onClose} />
                    </div>
                    <div className="modal-body p-0">
                        {loading && (
                            <div className="text-center py-5">
                                <div className="spinner-border" style={{ color: theme.primary }} role="status" />
                            </div>
                        )}
                        {!loading && logs.length === 0 && (
                            <div className="text-center py-5 text-muted">
                                <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                                No delivery logs yet. Fire a test or wait for an event trigger to run.
                            </div>
                        )}
                        {!loading && logs.length > 0 && (
                            <div className="table-responsive">
                                <table className="table mb-0" style={{ fontSize: 13 }}>
                                    <thead style={{ background: '#f8fafc' }}>
                                        <tr>
                                            <th className="px-4 py-3 fw-semibold text-muted">Date</th>
                                            <th className="py-3 fw-semibold text-muted">Event</th>
                                            <th className="py-3 fw-semibold text-muted">Status</th>
                                            <th className="py-3 fw-semibold text-muted">HTTP</th>
                                            <th className="py-3 fw-semibold text-muted">Time</th>
                                            <th className="py-3 fw-semibold text-muted">Attempt</th>
                                            <th className="py-3 fw-semibold text-muted">Details</th>
                                            <th className="py-3"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {logs.map(log => {
                                            const sc         = STATUS_CONFIG[log.status] || STATUS_CONFIG.pending;
                                            const isExpanded = expandedId === log.id;
                                            const hasDetails = !!(log.error_message || log.response_body || log.request_url);
                                            return (
                                                <React.Fragment key={log.id}>
                                                    <tr
                                                        style={{ cursor: hasDetails ? 'pointer' : 'default', background: isExpanded ? '#f8fafc' : undefined }}
                                                        onClick={() => hasDetails && toggleRow(log.id)}
                                                    >
                                                        <td className="px-4 py-3 text-muted" style={{ whiteSpace: 'nowrap' }}>
                                                            {new Date(log.created_at).toLocaleString()}
                                                        </td>
                                                        <td className="py-3">
                                                            <code style={{ fontSize: 12 }}>{log.event_key}</code>
                                                        </td>
                                                        <td className="py-3">
                                                            <span className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded fw-semibold"
                                                                style={{ background: sc.bg, color: sc.color, fontSize: 11 }}>
                                                                <i className={`bi ${sc.icon}`}></i> {sc.label}
                                                            </span>
                                                        </td>
                                                        <td className="py-3">
                                                            {log.response_status
                                                                ? <span className="fw-bold" style={{ color: log.status === 'success' ? '#166534' : '#b91c1c' }}>{log.response_status}</span>
                                                                : <span className="text-muted">—</span>
                                                            }
                                                        </td>
                                                        <td className="py-3 text-muted">
                                                            {log.response_time_ms != null ? `${log.response_time_ms}ms` : '—'}
                                                        </td>
                                                        <td className="py-3 text-muted">#{log.attempt_number}</td>
                                                        <td className="py-3">
                                                            {log.error_message ? (
                                                                <span style={{
                                                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                                                    background: '#fff5f5', color: '#b91c1c',
                                                                    border: '1px solid #fecaca', borderRadius: 5,
                                                                    padding: '2px 8px', fontSize: 11, fontWeight: 600,
                                                                }}>
                                                                    <i className="bi bi-exclamation-circle"></i> Error
                                                                </span>
                                                            ) : (
                                                                <span style={{
                                                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                                                    background: '#f0fdf4', color: '#166534',
                                                                    border: '1px solid #bbf7d0', borderRadius: 5,
                                                                    padding: '2px 8px', fontSize: 11, fontWeight: 600,
                                                                }}>
                                                                    <i className="bi bi-check-circle"></i> OK
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="py-3 text-end pe-3">
                                                            {hasDetails && (
                                                                <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'} text-muted`}
                                                                    style={{ fontSize: 13 }}></i>
                                                            )}
                                                        </td>
                                                    </tr>
                                                    {isExpanded && (
                                                        <LogDetailPanel
                                                            log={log}
                                                            onClose={() => setExpandedId(null)}
                                                        />
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer border-0 justify-content-center">
                        <Paginator
                            page={page}
                            pageCount={totalPages}
                            onPrevious={onPageChange}
                            onPage={onPageChange}
                            onNext={onPageChange}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── EmptyState ───────────────────────────────────────────────────────────────

function EmptyState({ theme, onAdd }) {
    return (
        <div className="text-center py-5">
            <div className="mb-3" style={{ fontSize: 64, opacity: 0.15 }}>
                <i className="bi bi-send-check"></i>
            </div>
            <h5 className="fw-bold text-dark mb-2">No webhook endpoints yet</h5>
            <p className="text-muted mb-4" style={{ maxWidth: 440, margin: '0 auto 1.5rem' }}>
                Webhook endpoints let Arcee push event data to any external system — CRMs, Slack,
                your own API, or automation tools like n8n or Make.com. Create one and attach it to an event trigger.
            </p>
            <button
                className="btn fw-semibold px-4"
                onClick={onAdd}
                style={{ background: theme.primary, color: '#fff', border: 'none', borderRadius: theme.buttonRadius }}
            >
                <i className="bi bi-plus-lg me-2"></i> Create your first endpoint
            </button>
        </div>
    );
}

// ─── SectionLabel ─────────────────────────────────────────────────────────────

function SectionLabel({ label }) {
    return (
        <div className="d-flex align-items-center gap-2 mb-3 mt-2">
            <div style={{ height: 1, flex: 1, background: '#e2e8f0' }} />
            <span className="text-uppercase fw-bold text-muted" style={{ fontSize: 10, letterSpacing: 1 }}>{label}</span>
            <div style={{ height: 1, flex: 1, background: '#e2e8f0' }} />
        </div>
    );
}
